// Shared types for the Internal Task Number Summary report.
// Tasks are grouped by their internal task number; a number whose tasks mix
// UOMs arrives as MULTIPLE group entries (one per UOM) — render as-is, so a
// group's identity is internalTaskNumber + uomName. The 'Unassigned' group
// (isUnassigned = true) collects tasks with a blank internal number.
// All money/qty/rate numerics arrive as STRINGS and gRPC may omit zero-valued
// fields, so every reader goes through `num` and defaults missing values to 0.

export {
  num,
  fmtINR,
  fmtINRCompact,
  fmtQty,
} from "../ThreeMConsolidation/threeMShared";

// Single source of the formula texts — the table header tooltips and the
// Excel note line must read identically. Three rate pairs with DIFFERENT
// formulas: Planned is qty-weighted off the client rate fed at task level;
// Executed is qty-weighted off the executed rate fed at task close;
// Executed-Planned derives from the task-wise 3M cost (Material + Manpower +
// Machinery fed at task level, same sources as the 3M Consolidation report),
// rounded to 2 decimals.
// Rates are the Basic Rate — the task rate net of GST (a pass-through) and BOCW
// labour cess (withheld by the client), which is what the project actually earns.
// This is the same basis as the 3M cost the Executed-Planned column is built from.
export const PLANNED_RATE_FORMULA =
  "Wtd Avg Rate (Planned) = Σ (Task Qty × Basic Rate) ÷ Σ Task Qty, computed per Internal Task Number. Planned Amount = Σ (Task Qty × Basic Rate). Basic Rate = task rate net of GST and labour cess.";
export const EXECUTED_RATE_FORMULA =
  "Wtd Avg Rate (Executed) = Σ (Task Qty × Executed Basic Rate) ÷ Σ Task Qty, computed per Internal Task Number. Executed Amount = Σ (Task Qty × Executed Basic Rate).";
export const EXECUTED_PLANNED_RATE_FORMULA =
  "Wtd Avg Rate (Executed-Planned) = Total 3M cost (Material + Manpower + Machinery fed at task level) ÷ Σ Task Qty, computed per Internal Task Number, 2 decimals. Executed-Planned Amount = Total 3M cost.";
// Combined text for the Excel note line.
export const WEIGHTED_RATE_FORMULA = `${PLANNED_RATE_FORMULA} ${EXECUTED_RATE_FORMULA} ${EXECUTED_PLANNED_RATE_FORMULA}`;

export interface InternalTaskRow {
  taskId?: number;
  taskSequenceId?: string;
  taskName?: string;
  taskStatus?: string;
  taskCompletionPercentage?: string;
  phaseId?: number;
  phaseName?: string;
  activityId?: number;
  activityName?: string;
  workQuantity?: string;
  uomName?: string;
  /** Planned rate fed at task level, net of GST and labour cess (Basic Rate). */
  rate?: string;
  /** Executed rate fed at task close, net of GST and labour cess. */
  executedRate?: string;
  plannedAmount?: string;
  /** qty × executedRate, 2dp string. */
  executedAmount?: string;
  /** Task 3M cost ÷ task qty, 2dp string. */
  executedPlannedRate?: string;
  /** Task-wise total 3M cost, 2dp string. */
  executedPlannedAmount?: string;
}

export interface InternalTaskGroup {
  /** 'Unassigned' for the blank-number group. */
  internalTaskNumber?: string;
  isUnassigned?: boolean;
  uomName?: string;
  taskCount?: number;
  /** 4dp string. */
  totalQuantity?: string;
  /** Σ(qty × planned rate) ÷ Σqty, 4dp string. */
  weightedPlannedRate?: string;
  /** Σ(qty × planned rate), 2dp string. */
  plannedAmount?: string;
  /** Σ(qty × executed rate) ÷ Σqty, 4dp string. */
  weightedExecutedRate?: string;
  /** Σ(qty × executed rate), 2dp string. */
  executedAmount?: string;
  /** Σ task-wise 3M cost ÷ Σqty, 2dp string. */
  weightedExecutedPlannedRate?: string;
  /** Σ task-wise 3M cost, 2dp string. */
  executedPlannedAmount?: string;
  /** Embedded task rows — one fetch powers the table AND the Excel export. */
  tasks?: InternalTaskRow[];
  // The internal task number IS a BOQ code, so the server resolves it to a norm
  // group and to the BOQ's own item description. All blank on the Unassigned
  // group and on any code the dictionary does not know.
  level1Code?: string;
  level1Name?: string;
  groupCode?: string;
  groupName?: string;
  /** The code's own description, e.g. 'Class I, II, & III'. */
  normTitle?: string;
}

export interface InternalTaskSummaryData {
  projectName?: string;
  groupCount?: number;
  taskCount?: number;
  unassignedTaskCount?: number;
  /** Grand total ₹, 2dp string. */
  plannedAmount?: string;
  /** Grand total ₹, 2dp string. */
  executedAmount?: string;
  /** Grand total 3M cost ₹, 2dp string. */
  executedPlannedAmount?: string;
  groups?: InternalTaskGroup[];
}

// Stable content-based group identity (number + UOM — see mixed-UOM note
// above): table row keys survive client-side search filtering, so expanded
// groups stay expanded while the user types.
export const internalTaskGroupKey = (group: InternalTaskGroup): string =>
  `GROUP-${group.internalTaskNumber ?? ""}::${group.uomName ?? ""}`;
